/* eslint-disable no-param-reassign */
import Alert from 'components/common/Alert';
import LoadingOverlay from 'components/common/LoadingOverlay';
import useGetFinancing from 'hooks/financing/useFinancing';
import useSubmitLoan from 'hooks/financing/useSubmitLoan';
import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { routePatterns } from 'router/route-paths';
import { useIntl } from 'react-intl';
import WarningMessage from 'components/common/WarningMessage';
import { Col, Row } from 'react-flexbox-grid';
import LoanCalculator from './LoanCalculator';

const useSubmitLoanHandler = (projectId) => {
  const loan = useSubmitLoan();
  const history = useHistory();

  const handleSubmit = useCallback(
    async (variables, { setSubmitting }) => {
      try {
        delete variables.price;
        await loan({ projectId, formData: variables });
        history.push(routePatterns.loan.stringify({ id: projectId }));
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSubmitting(false);
      }
    },
    [history, loan, projectId],
  );

  return handleSubmit;
};

export default () => {
  const { id: projectId } = useParams();
  const { formatMessage: t } = useIntl();
  const [{ project }, loading] = useGetFinancing(projectId);
  const onSubmit = useSubmitLoanHandler(projectId);

  if (loading) return <LoadingOverlay />;

  if (!project) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'app.project_not_found' })}
      </Alert>
    );
  }

  if (project.price === 0) {
    return (
      <Row center="xs">
        <Col md={8} lg={6}>
          <WarningMessage
            title={t({ id: 'page_titles.loan' })}
            message={t({ id: 'project_plan.complete_previous_step' })}
            btnLabel={t({ id: 'project_plan.complete_previous_step_btn' })}
            btnLink={routePatterns.projectDetailsCalculator.stringify({ id: projectId })}
          />
        </Col>
      </Row>
    );
  }

  const initialValues = {
    price: project.price,
    budget: project.estimated_property_price ? project.budget - project.estimated_property_price : project.budget,
    period: project.loan?.period || 10,
    interest_type: project.loan?.interest_type || 'variable',
    fixed_interest_period: project.loan?.fixed_interest_period || 10,
    ...(Number.isFinite(project.estimated_property_price)
      ? { estimated_property_price: project.estimated_property_price }
      : {}),
  };

  return <LoanCalculator {...{ project, t, initialValues, onSubmit }} />;
};
